import {
  FLUSH,
  PAUSE,
  PURGE,
  PERSIST,
  REGISTER,
  REHYDRATE,
  persistStore,
  persistReducer,
  createTransform,
} from 'redux-persist';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import createWebStorage from 'redux-persist/lib/storage/createWebStorage';
import localforage from 'localforage';
import LZString from 'lz-string';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import type { Middleware } from '@reduxjs/toolkit';

// Reducers
import uiReducer from './slices/ui';
import authReducer from './slices/auth';
import homeReducer from './slices/home';
import albumReducer from './slices/album';
import queueReducer from './slices/queue';
import genreReducer from './slices/genre';
import searchReducer from './slices/search';
import browseReducer from './slices/browse';
import artistReducer from './slices/artist';
import profileReducer from './slices/profile';
import spotifyReducer from './slices/spotify';
import languageReducer from './slices/language';
import playlistReducer from './slices/playlist';
import likedSongsReducer from './slices/likedSongs';
import playingNowReducer from './slices/playingNow';
import yourLibraryReducer, { type YourLibraryState } from './slices/yourLibrary';
import bootPhaseReducer from './slices/bootPhase';
import searchHistoryReducer from './slices/searchHistory';
import artistDiscographyReducer from './slices/discography';
import feedbackReducer from './slices/feedback';
import composerReducer from './slices/composer';
import composerWorksReducer from './slices/composerWorks';
import composerRecordingsReducer from './slices/composerRecordings';
import editPlaylistModalReducer from './slices/editPlaylistModal';
import youtubeMatchesReducer from './slices/youtubeMatches';
import youtubePlaybackReducer from './slices/youtubePlayback';
import { mutedEntitiesReducer } from './slices/mutedEntities';
import { preferencesReducer } from './slices/preferences';
import onboardingReducer from './slices/onboarding';
import expireReducer from 'redux-persist-expire';

const createNoopStorage = () => ({
  getItem: (_key: string) => Promise.resolve(null),
  setItem: (_key: string, value: unknown) => Promise.resolve(value),
  removeItem: (_key: string) => Promise.resolve(),
});

/**
 * Hybrid storage:
 * - Reads synchronously from localStorage (for fast boot) when available.
 * - Persists to IndexedDB via localforage to avoid localStorage quota issues.
 * - Mirrors writes to localStorage best-effort; failures (quota) are swallowed.
 */
const createPersistStorage = () => {
  if (typeof window === 'undefined') return createNoopStorage();

  const localStorageBackend = createWebStorage('local');
  const idbBackend = localforage;
  // Toggle to test IDB-only persistence; when false, we skip the localStorage fast-path reads/writes.
  const USE_LOCAL_FAST_PATH = false;

  const readLocal = async (key: string) => {
    try {
      return await localStorageBackend.getItem(key);
    } catch {
      return null;
    }
  };

  return {
    // Prefer the localStorage snapshot (fast), fall back to IndexedDB.
    getItem: async (key: string) => {
      if (USE_LOCAL_FAST_PATH) {
        const localValue = await readLocal(key);
        if (localValue != null) {
          if (process.env.NODE_ENV === 'development') {
            console.info('[RECFETCH] persist get local hit', {
              key,
              size: typeof localValue === 'string' ? localValue.length : null,
            });
          }
          return localValue;
        }
      }

      try {
        const idbValue = await idbBackend.getItem<string>(key);
        if (idbValue != null) {
          if (process.env.NODE_ENV === 'development') {
            console.info('[RECFETCH] persist get idb hit', {
              key,
              size: typeof idbValue === 'string' ? idbValue.length : null,
            });
          }
          // Cache for next fast rehydrate; ignore quota failures.
          try {
            await localStorageBackend.setItem(key, idbValue);
          } catch {
            // ignore
          }
          return idbValue;
        }
      } catch {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[RECFETCH] persist get idb error', { key });
        }
        return null;
      }

      return null;
    },
    setItem: async (key: string, value: unknown) => {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
      if (USE_LOCAL_FAST_PATH) {
        try {
          await localStorageBackend.setItem(key, stringValue);
          if (process.env.NODE_ENV === 'development') {
            console.info('[RECFETCH] persist set local ok', {
              key,
              size: stringValue.length,
            });
          }
        } catch {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[RECFETCH] persist set local failed', {
              key,
              size: stringValue.length,
              error: 'localStorage set failed',
            });
          }
          // ignore quota errors; IndexedDB write below still succeeds
        }
      }
      try {
        await idbBackend.setItem(key, stringValue);
        if (process.env.NODE_ENV === 'development') {
          console.info('[RECFETCH] persist set idb ok', {
            key,
            size: stringValue.length,
          });
        }
      } catch {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[RECFETCH] persist set idb failed', {
            key,
            size: stringValue.length,
            error: 'idb set failed',
          });
        }
        // swallow to avoid crashing persist
      }
      return stringValue;
    },
    removeItem: async (key: string) => {
      try {
        await localStorageBackend.removeItem(key);
      } catch {
        // ignore
      }
      try {
        await idbBackend.removeItem(key);
      } catch {
        // ignore
      }
    },
  };
};

const storage = createPersistStorage();

// Composer/library slices regularly hold thousands of records and blow past the default
// Redux Toolkit dev-middleware budget. Opt them out of the expensive invariant checks.
const LARGE_STATE_PATHS: ReadonlyArray<string> = [
  'composerWorks.byComposerId',
  'composerRecordings.byComposerId',
  'composerRecordings.byRecordingId',
  'yourLibrary.myAlbums',
  'yourLibrary.myArtists',
  'yourLibrary.myPlaylists',
  'yourLibrary.myComposers',
  'yourLibrary.myWorks',
  'yourLibrary.myRecordings',
];

const serializableCheckIgnoredPaths: Array<string | RegExp> = [
  'spotify.player',
  ...LARGE_STATE_PATHS,
];

const immutableCheckConfig =
  process.env.NODE_ENV === 'production'
    ? false
    : {
        warnAfter: 64,
        ignoredPaths: LARGE_STATE_PATHS,
      };

const appReducer = combineReducers({
  ui: uiReducer,
  auth: authReducer,
  home: homeReducer,
  album: albumReducer,
  queue: queueReducer,
  genre: genreReducer,
  search: searchReducer,
  artist: artistReducer,
  browse: browseReducer,
  profile: profileReducer,
  spotify: spotifyReducer,
  language: languageReducer,
  playlist: playlistReducer,
  playingNow: playingNowReducer,
  likedSongs: likedSongsReducer,
  yourLibrary: yourLibraryReducer,
  bootPhase: bootPhaseReducer,
  searchHistory: searchHistoryReducer,
  feedback: feedbackReducer,
  artistDiscography: artistDiscographyReducer,
  editPlaylistModal: editPlaylistModalReducer,
  composer: composerReducer,
  composerWorks: composerWorksReducer,
  composerRecordings: composerRecordingsReducer,
  youtubeMatches: youtubeMatchesReducer,
  youtubePlayback: youtubePlaybackReducer,
  mutedEntities: mutedEntitiesReducer,
  preferences: preferencesReducer,
  onboarding: onboardingReducer,
});

// @ts-ignore
const rootReducer = (state, action) => {
  if (action.type === 'auth/removeUser') {
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

const yourLibrarySearchResetTransform = createTransform<YourLibraryState, YourLibraryState>(
  (inboundState) => {
    if (!inboundState) return inboundState;
    return { ...inboundState, search: '', filter: 'ALL' };
  },
  (outboundState) => {
    if (!outboundState) return outboundState;
    const nextState = { ...outboundState };
    if (outboundState.librarySyncSettings) {
      const jobId = outboundState.librarySyncSettings.recordingsAndWorksJobId;
      const persistedJob = outboundState.librarySyncSettings.recordingsAndWorksJob;
      const hasRunningJob =
        Boolean(jobId) ||
        Boolean(
          persistedJob &&
            (persistedJob.status === 'queued' || persistedJob.status === 'running')
        );
      nextState.librarySyncSettings = {
        ...outboundState.librarySyncSettings,
        status: 'idle',
        updateStatus: 'idle',
        error: null,
        pendingUpdate: null,
        isSyncingAlbumTracks: false,
        isSyncingAlbumRecordings: false,
        isSyncingRecordingWorks: false,
        isSyncingAlbumRecordingsAndWorks: hasRunningJob,
        recordingsAndWorksJob: null,
        recordingsAndWorksJobId: jobId ?? null,
      };
    }
    return nextState;
  },
  { whitelist: ['yourLibrary'] }
);

const mutedEntitiesPersistTransform = createTransform(
  (inboundState: any) => {
    if (!inboundState) return inboundState;
    return { ...inboundState, pending: {}, error: null };
  },
  (outboundState: any) => {
    if (!outboundState) return outboundState;
    const status = outboundState.lastLoadedAt ? 'succeeded' : 'idle';
    return { ...outboundState, pending: {}, error: null, status };
  },
  { whitelist: ['mutedEntities'] }
);

const preferencesPersistTransform = createTransform(
  (inboundState: any) => inboundState,
  (outboundState: any) => {
    if (!outboundState) return outboundState;
    const status = outboundState.lastLoadedAt ? 'succeeded' : 'idle';
    return { ...outboundState, error: null, status };
  },
  { whitelist: ['preferences'] }
);

const whitelist = [
  'language',
  'ui',
  'searchHistory',
  // 'yourLibrary',
  // 'composer',
  // 'composerWorks',
  // 'composerRecordings',
  'mutedEntities',
  'preferences',
  'onboarding',
] as string[];

const persistedReducer = persistReducer(
  {
    storage,
    whitelist,
    key: 'root',
    transforms: [
      expireReducer('searchHistory', { expireSeconds: 60 * 60 * 24 }),
      yourLibrarySearchResetTransform,
      mutedEntitiesPersistTransform,
      preferencesPersistTransform,
      // Compress whitelisted slices; safely fall back to plain JSON if data is uncompressed.
      createTransform(
        (inboundState: unknown) => LZString.compressToUTF16(JSON.stringify(inboundState)),
        (outboundState: unknown) => {
          if (typeof outboundState !== 'string') return outboundState;

          try {
            const decompressed = LZString.decompressFromUTF16(outboundState);
            if (decompressed) {
              return JSON.parse(decompressed);
            }
          } catch {
            // fall through to plain JSON parse
          }

          try {
            return JSON.parse(outboundState);
          } catch {
            return null;
          }
        },
        { whitelist }
      ),
    ],
  },
  rootReducer
);

const recfetchPersistLogger: Middleware = () => (next) => (action) => {
  const type = (action as any)?.type;
  if (
    process.env.NODE_ENV === 'development' &&
    (type === REHYDRATE || type === PERSIST)
  ) {
    const payload: any = (action as any).payload;
    const lib = payload?.yourLibrary;
    const lastFetched = lib?.lastFetchedAt?.recordings ?? null;
    const recordingsCount = Array.isArray(lib?.myRecordings) ? lib.myRecordings.length : null;
    // eslint-disable-next-line no-console
    console.info('[RECFETCH] persist action', {
      type,
      lastFetched,
      recordingsCount,
    });
  }
  return next(action);
};

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: immutableCheckConfig,
      serializableCheck: {
        ignoredPaths: serializableCheckIgnoredPaths,
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        warnAfter: 64,
      },
    }).concat(recfetchPersistLogger),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const persistor = persistStore(store);
